import React, { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import Container from '@embracesbs/component-container'
import Button2 from '@embracesbs/component-button2'
import Text from '@embracesbs/component-text'
import EmptyState from '@embracesbs/component-emptystate'
import DarkTabs from '../Components/Common/DarkTabs'
import { DEVELOPER_CENTER_PATH } from '../Components/MainMenu/navigation'
import LoadingState from '../Components/Common/LoadingState'
import CostReportView from '../Components/Reports/CostReportView'
import KrrReportView from '../Components/Reports/KrrReportView'
import TrivyReportView from '../Components/Reports/TrivyReportView'
import { useApi } from '../Hooks'
import { downloadFile, formatDateTime } from '../Utilities/helpers/helpers'

interface ReportSummary {
  reportType: string
  id: string
  generatedAt: string
}

interface SelectedReport {
  reportType: string
  id: string
  content: unknown
}

const REPORT_VIEWERS: Record<string, React.FC<{ content: unknown }>> = {
  cost: CostReportView,
  krr: KrrReportView,
  trivy: TrivyReportView
}

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1)

const prettyPrint = (content: unknown): string => {
  try {
    return JSON.stringify(content, null, 2)
  } catch {
    return String(content)
  }
}

const Reports: React.FC = () => {
  const { fetchApi } = useApi()
  const [searchParams, setSearchParams] = useSearchParams()
  const appliedDeepLinkRef = useRef(false)

  const [reportTypes, setReportTypes] = useState<string[]>([])
  const [selectedType, setSelectedType] = useState<string>('')
  const [isLoadingTypes, setIsLoadingTypes] = useState(true)

  const [reports, setReports] = useState<ReportSummary[]>([])
  const [isLoadingReports, setIsLoadingReports] = useState(false)

  const [view, setView] = useState<'list' | 'report'>('list')
  const [selectedReport, setSelectedReport] = useState<SelectedReport | null>(null)
  const [isLoadingContent, setIsLoadingContent] = useState(false)

  useEffect(() => {
    const fetchTypes = async () => {
      setIsLoadingTypes(true)
      try {
        const types: string[] = (await fetchApi({ endpoint: '/api/reports/types', method: 'GET' })) ?? []
        setReportTypes(types)
        if (types.length > 0) setSelectedType(types[0])
      } catch (error) {
        console.error('Failed to load report types', error)
      } finally {
        setIsLoadingTypes(false)
      }
    }

    fetchTypes()
  }, [fetchApi])

  useEffect(() => {
    if (!selectedType) return

    const fetchReports = async () => {
      setIsLoadingReports(true)
      setView('list')
      setSelectedReport(null)
      try {
        const items: ReportSummary[] =
          (await fetchApi({ endpoint: `/api/reports/${encodeURIComponent(selectedType)}`, method: 'GET' })) ?? []
        setReports(items)
      } catch (error) {
        console.error(`Failed to load reports for ${selectedType}`, error)
        setReports([])
      } finally {
        setIsLoadingReports(false)
      }
    }

    fetchReports()
  }, [selectedType, fetchApi])

  // Supports direct links to a tab (e.g. /reports?type=trivy) and to a specific report
  // within it (e.g. /reports?type=trivy&id=<report-id>).
  useEffect(() => {
    if (isLoadingTypes || reportTypes.length === 0 || appliedDeepLinkRef.current) return
    const linkType = searchParams.get('type')
    const linkId = searchParams.get('id')
    if (!linkType && !linkId) return
    appliedDeepLinkRef.current = true

    const resolvedType = linkType || reportTypes[0]
    setSelectedType(resolvedType)

    if (!linkId) return

    const openLinkedReport = async () => {
      setIsLoadingContent(true)
      try {
        const content = await fetchApi({
          endpoint: `/api/reports/${encodeURIComponent(resolvedType)}/${encodeURIComponent(linkId)}`,
          method: 'GET'
        })
        setSelectedReport(content !== undefined && content !== null ? { reportType: resolvedType, id: linkId, content } : null)
      } catch (error) {
        console.error(`Failed to load linked report ${linkId}`, error)
        setSelectedReport(null)
      } finally {
        setView('report')
        setIsLoadingContent(false)
      }
    }

    openLinkedReport()
  }, [isLoadingTypes, reportTypes, searchParams, setSearchParams, fetchApi])

  const openReport = async (id: string) => {
    setIsLoadingContent(true)
    setView('report')
    setSearchParams({ type: selectedType, id })
    try {
      const content = await fetchApi({
        endpoint: `/api/reports/${encodeURIComponent(selectedType)}/${encodeURIComponent(id)}`,
        method: 'GET'
      })
      setSelectedReport(content !== undefined && content !== null ? { reportType: selectedType, id, content } : null)
    } catch (error) {
      console.error(`Failed to load report ${id}`, error)
      setSelectedReport(null)
    } finally {
      setIsLoadingContent(false)
    }
  }

  const copyReportLink = () => {
    if (!selectedReport) return
    navigator.clipboard?.writeText(window.location.href)
  }

  const downloadSelectedReport = () => {
    if (!selectedReport) return
    downloadFile(
      `${selectedReport.reportType}-${selectedReport.id}.json`,
      prettyPrint(selectedReport.content),
      'application/json;charset=utf-8'
    )
  }

  if (isLoadingTypes) {
    return (
      <div style={{ height: 'var(--app-height, 100vh)' }}>
        <LoadingState text='Loading report types…' />
      </div>
    )
  }

  if (reportTypes.length === 0) {
    return (
      // Unlike the loading state above this one is terminal: without the band
      // the only way off the page is the rail, so it keeps the back arrow. No
      // tabs to show, hence the empty set.
      <div style={{ display: 'flex', flexDirection: 'column', height: 'var(--app-height, 100vh)' }}>
        <DarkTabs tabs={[]} activeId='' onTabClick={() => undefined} backTo={DEVELOPER_CENTER_PATH} />
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <EmptyState
            imageName='emptyfolder'
            size='medium'
            title='No reports available'
            text='No report types are configured for this environment.'
          />
        </div>
      </div>
    )
  }

  const Viewer = selectedReport ? REPORT_VIEWERS[selectedReport.reportType] : undefined

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'var(--app-height, 100vh)', overflow: 'hidden' }}>
      <DarkTabs
        tabs={reportTypes.map((type) => ({ id: type, text: capitalize(type) }))}
        backTo={DEVELOPER_CENTER_PATH}
        activeId={selectedType}
        onTabClick={(type) => {
          setSelectedType(type)
          setSearchParams({})
        }}
      />

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '16px 20px' }} className='custom-scrollbar'>
        {isLoadingContent && view === 'list' ? (
          <LoadingState text='Loading report…' />
        ) : view === 'list' ? (
          isLoadingReports ? (
            <LoadingState text={`Loading ${selectedType} reports…`} />
          ) : reports.length === 0 ? (
            <EmptyState imageName='emptyfolder' size='medium' title='No reports found' text={`No ${selectedType} reports have run yet.`} />
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '10px' }}>
              {reports.map((report) => (
                <button
                  key={report.id}
                  type='button'
                  onClick={() => openReport(report.id)}
                  style={{
                    cursor: 'pointer',
                    textAlign: 'left',
                    borderStyle: 'solid',
                    borderWidth: '1px',
                    borderColor: 'var(--tma-list-card-border)',
                    borderRadius: '6px',
                    background: 'var(--tma-panel-bg)',
                    padding: '10px 14px'
                  }}
                >
                  <Text textStyle='h6' style={{ fontSize: '14px', marginBottom: '4px' }}>
                    {formatDateTime(report.generatedAt) || report.id}
                  </Text>
                  <Text textStyle='description' textColor='grey'>
                    {report.id}
                  </Text>
                </button>
              ))}
            </div>
          )
        ) : (
          <div>
            <Container justify='space-between' isFullWidth style={{ marginBottom: '14px' }}>
              <Button2
                text='← Back to reports'
                onClick={() => {
                  setView('list')
                  setSearchParams({})
                }}
                variant='outlined'
                type='button'
              />
              <Container style={{ gap: '8px' }}>
                <Button2 text='Copy link' onClick={copyReportLink} isDisabled={!selectedReport} variant='outlined' type='button' />
                <Button2 text='Download' onClick={downloadSelectedReport} isDisabled={!selectedReport} variant='outlined' type='button' />
              </Container>
            </Container>

            {isLoadingContent ? (
              <LoadingState text='Loading report…' />
            ) : !selectedReport ? (
              <Text textColor='grey' align='center'>
                This report could not be loaded.
              </Text>
            ) : Viewer ? (
              <Viewer content={selectedReport.content} />
            ) : (
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '12px' }}>{prettyPrint(selectedReport.content)}</pre>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default Reports
